// Modelo de la app «Leads»: etapas, fechas en el día civil de la org, etiquetas
// y exportación. Sin estado ni red: lo usan la lista, el embudo y la ficha.

import type { LeadDTO } from '@/modules/leads-import';

export type StageKey = 'new' | 'contacted' | 'qualified' | 'converted' | 'discarded';

/** Etapas del embudo en orden (valores = `Lead.status` del backend). `tone` es
 *  la clase de color del chip/columna (leads-app.css, con variante oscura AA). */
export const STAGES: { key: StageKey; label: string; hint: string; tone: string }[] = [
  { key: 'new', label: 'Nuevo', hint: 'Llegó y nadie lo ha contactado', tone: 'la-stage-new' },
  { key: 'contacted', label: 'Contactado', hint: 'Ya se le escribió o llamó', tone: 'la-stage-contacted' },
  { key: 'qualified', label: 'Calificado', hint: 'Tiene interés real y encaja', tone: 'la-stage-qualified' },
  { key: 'converted', label: 'Ganado', hint: 'Compró o firmó', tone: 'la-stage-converted' },
  { key: 'discarded', label: 'Descartado', hint: 'No sigue (sin interés, duplicado, spam)', tone: 'la-stage-discarded' },
];

export const STAGE_BY_KEY: Record<string, (typeof STAGES)[number]> = Object.fromEntries(STAGES.map((s) => [s.key, s]));
export const stageLabel = (k?: string) => STAGE_BY_KEY[k || '']?.label || k || '—';

/** Zona de la org para «hoy / esta semana» (los leads de hoy son los del día civil). */
export const TZ = 'America/Mexico_City';

function parse(iso?: string | null): Date | null {
  if (!iso) return null;
  const s = String(iso);
  const d = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** YYYY-MM-DD del día civil de la org, `daysAgo` días atrás. */
export function civilDay(daysAgo = 0): string {
  return new Date(Date.now() - daysAgo * 86_400_000).toLocaleDateString('en-CA', { timeZone: TZ });
}

export function civilKey(iso?: string | null): string {
  const d = parse(iso);
  return d ? d.toLocaleDateString('en-CA', { timeZone: TZ }) : '';
}

export type DateRange = '' | 'hoy' | '7d' | '30d';
export const DATE_RANGES: { key: DateRange; label: string }[] = [
  { key: '', label: 'Cualquier fecha' },
  { key: 'hoy', label: 'Hoy' },
  { key: '7d', label: 'Últimos 7 días' },
  { key: '30d', label: 'Últimos 30 días' },
];
/** Primer día civil incluido en el rango. */
export function rangeStart(r: DateRange): string | null {
  return r === 'hoy' ? civilDay(0) : r === '7d' ? civilDay(6) : r === '30d' ? civilDay(29) : null;
}

/** «Hace 5 min» / «14:05» / «Ayer» / «12 oct» */
export function shortWhen(iso?: string | null): string {
  const d = parse(iso);
  if (!d) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const k = civilKey(iso);
  if (k === civilDay(0)) return d.toLocaleTimeString('es-MX', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  if (k === civilDay(1)) return 'Ayer';
  const sameYear = k.slice(0, 4) === civilDay(0).slice(0, 4);
  return d.toLocaleDateString('es-MX', { timeZone: TZ, day: 'numeric', month: 'short', ...(sameYear ? {} : { year: '2-digit' }) });
}

export function longWhen(iso?: string | null): string {
  const d = parse(iso);
  return d ? d.toLocaleString('es-MX', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

export function ageHours(iso?: string | null): number {
  const d = parse(iso);
  return d ? (Date.now() - d.getTime()) / 3_600_000 : 0;
}

/** Un registro migrado de un ERP (trae ID del sistema origen) es un CLIENTE. */
export function isClientRecord(lead: LeadDTO): boolean {
  return !!((lead as any).external_id || lead.business_context?.source_row?.numero);
}

export function leadTitle(lead: LeadDTO): string {
  return lead.name || lead.email || lead.phone || 'Sin nombre';
}

export function leadCompany(lead: LeadDTO): string {
  const bc = (lead.business_context || {}) as Record<string, any>;
  return [bc.company_name, bc.region].filter(Boolean).join(' · ');
}

export function assigneeLabel(lead: LeadDTO, names: Record<string, string>): string {
  if (lead.assigned_to_user_id && names[lead.assigned_to_user_id]) return names[lead.assigned_to_user_id];
  return lead.assigned_to_email || lead.assigned_to_user_id || '';
}

export function isUnassigned(lead: LeadDTO): boolean {
  return !lead.assigned_to_user_id && !lead.assigned_to_email;
}

/** «Sin respuesta»: sigue en Nuevo después de 24 h (nadie lo ha movido). */
export function isUnanswered(lead: LeadDTO): boolean {
  return lead.status === 'new' && ageHours(lead.created_at) >= 24;
}

/** Origen legible del primer toque (canal + campaña). */
export function originLine(lead: LeadDTO): string {
  const a = lead.attribution || {};
  const canal = lead.canal_etiqueta || lead.canal || '';
  return [canal, a.utm_campaign && `«${a.utm_campaign}»`].filter(Boolean).join(' · ');
}

export function originRows(lead: LeadDTO): [string, string][] {
  const a = lead.attribution || {};
  const rows: [string, string | undefined][] = [
    ['Canal', lead.canal_etiqueta || lead.canal || 'Sin registrar'],
    ['Fuente (alta)', lead.source],
    ['Campaña', a.utm_campaign],
    ['utm_source', a.utm_source],
    ['utm_medium', a.utm_medium],
    ['utm_content', a.utm_content],
    ['utm_term', a.utm_term],
    ['Llegó desde', a.referrer_domain],
    ['Primera página', a.landing_path],
    ['Clic de anuncio', a.gclid ? 'Google Ads' : a.fbclid ? 'Meta' : undefined],
  ];
  return rows.filter((r): r is [string, string] => !!r[1]);
}

/** Solo dígitos, últimos 10 (para buscar el mismo teléfono en otras apps). */
export function phoneDigits(raw?: string | null): string {
  return String(raw || '').replace(/\D/g, '').slice(-10);
}

/** CSV con BOM (Excel lo abre con acentos bien). */
export function leadsToCsv(rows: LeadDTO[], names: Record<string, string>): string {
  const head = ['Nombre', 'Email', 'Teléfono', 'Etapa', 'Canal', 'Campaña', 'utm_source', 'utm_medium', 'Fuente', 'Vendedora', 'Creado', 'Actualizado', 'Notas', 'ID'];
  const esc = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((l) => {
    const a = l.attribution || {};
    return [l.name, l.email, l.phone, stageLabel(l.status), l.canal_etiqueta || l.canal || '', a.utm_campaign, a.utm_source, a.utm_medium,
      l.source, assigneeLabel(l, names), longWhen(l.created_at), longWhen(l.updated_at), l.notes, l.id].map(esc).join(',');
  });
  return '﻿' + [head.join(','), ...lines].join('\r\n');
}

export function downloadText(filename: string, text: string, type = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Etiquetas para los datos importados (business_context/extra); el resto se humaniza.
const FIELD_LABELS: Record<string, string> = {
  direccion: 'Dirección', contacto: 'Contacto', vendedor: 'Vendedor', total_facturado: 'Total facturado',
  ult_importe: 'Último importe', ult_articulo: 'Último artículo', numero_eventos: 'Número de eventos',
  dias_sin_facturar: 'Días sin facturar', fecha_ult_movimiento: 'Último movimiento', phone_normalized: 'Tel. normalizado',
  phone_shared: 'Tel. compartido con otro cliente', phone_quality: 'Calidad del teléfono', telefono_2: 'Teléfono 2',
  email_normalized: 'Email normalizado', company_name: 'Empresa', industry: 'Industria', role: 'Cargo', region: 'Región',
  company_size: 'Tamaño de empresa', pain_hint: 'Necesidad',
};
// Internos o ya mostrados en otra sección de la ficha.
const HIDDEN_KEYS = new Set(['enriched_at', 'enriched_at_llm', 'source_row', 'canal', 'attribution', 'first_touch']);

export function extraFields(lead: LeadDTO): [string, string][] {
  const merged: Record<string, any> = { ...(lead.business_context || {}), ...(lead.extra || {}) };
  return Object.entries(merged)
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && v !== '' && v != null && !(typeof v === 'object' && !Object.keys(v).length))
    .map(([k, v]) => [
      FIELD_LABELS[k] ?? k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      typeof v === 'object' ? JSON.stringify(v) : String(v),
    ]);
}

/** Texto humano para un evento del timeline auditado. */
export function auditLabel(action: string): string {
  const m: Record<string, string> = {
    created: 'Lead creado', assigned: 'Asignado', reassigned: 'Reasignado', status_changed: 'Cambio de etapa',
    edited: 'Datos editados', note_added: 'Nota', presentation_generated: 'Presentación generada',
    email_sent: 'Correo enviado', agent_action: 'Acción del agente',
  };
  return m[action] || action.replace(/_/g, ' ');
}

export function readPref(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
export function writePref(key: string, v: string) {
  try { window.localStorage.setItem(key, v); } catch { /* sin almacenamiento: no pasa nada */ }
}
